import { gql, useApolloClient } from '@apollo/client';
import firebase from 'firebase/app';
import throttle from 'lodash/throttle';
import React, {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { IUser } from '../Party';
import { IS_PRODUCTION, onLogOut } from '../firebase/firebase';
import {
  EAreaPrefix,
  IParticipantForUser,
  IPartyForUser,
  TAreaKey,
  TUserId,
} from '../PartyClientResponse';
import { DEMO_AREAS } from '../components/party/PartyStage';
import { MAX_PEERS } from '../ApiConstants';
import { TParticipantsByArea } from '../components/party/PartyAreaBackground';
import { getIdToken, onSettingsUpdate } from '../controller/PartyDB';
import { clientInviteParticipantToArea } from '../controller/client/PartyClient';
import amplitudeJs from 'amplitude-js';
import { LOG_ERROR } from '../services/LoggingService';

export enum AuthState {
  LoggedIn,
  FetchingUser,
  LoggedOut,
}

interface ISpotlightPosition {
  top: number;
  left: number;
  width: number;
  height: number;
  ref: React.RefObject<HTMLDivElement> | null;
}

export interface IAppContextState {
  isTwitchChatOpen: boolean;
  user: (IUser & firebase.User) | null;
  masterVolume: number;
  masterMuted: boolean;
  enableAV: boolean;
  authState: AuthState;
  cockpitMode: boolean;
  spotlightPosition: ISpotlightPosition;
  mainVideoPosition: ISpotlightPosition;
  fakeUsers: IParticipantForUser[];
  party: IPartyForUser | null;
  isAudioInputBlocked: boolean;
  isSharingScreen: boolean;
  isChatBoxVisible: boolean;
}

export interface IDerivedAppState {
  participants: IParticipantForUser[];
  allParticipants: IParticipantForUser[];
  participantsByArea: TParticipantsByArea;
  inviteParticipantToArea: (inviteeId: TUserId) => Promise<void>;
  currentArea: TAreaKey;
  currentParticipant: IParticipantForUser | null;
  isHost: boolean;
  isMod: boolean;
}

export enum ActionType {
  SetTwitchChatOpenState,
  SetUser,
  UpdateUser,
  SetVolume,
  ToggleAV,
  ResetStore,
  FirebaseUserReceived,
  FetchFirebaseAuth,
  ToggleCockpitMode,
  SetSpotlightPosition,
  SetMainVideoPosition,
  SetFakeUsers,
  MoveRandomFakeUser,
  SetParty,
  InviteFakeUserToArea,
  SetUserDevAdmin,
  SetIsAudioInputBlocked,
  MuteMaster,
  SetShareScreen,
  SetChatBoxVisible,
}

export type TAction =
  | {
      type: ActionType.SetTwitchChatOpenState;
      isTwitchChatOpen: boolean;
    }
  | {
      type: ActionType.SetUser;
      user: (firebase.User & IUser) | null;
    }
  | {
      type: ActionType.UpdateUser;
      user: Partial<firebase.User & IUser> | null;
    }
  | {
      type: ActionType.ResetStore;
    }
  | {
      type: ActionType.SetVolume;
      volume: number;
    }
  | {
      type: ActionType.MuteMaster;
      muted: boolean;
    }
  | {
      type: ActionType.SetUserDevAdmin;
      isAdmin: boolean;
    }
  | {
      type: ActionType.FetchFirebaseAuth;
    }
  | {
      type: ActionType.InviteFakeUserToArea;
      fakeUserId: TUserId;
      areaKey: TAreaKey;
    }
  | {
      type: ActionType.ToggleCockpitMode;
      enable: boolean;
    }
  | {
      type: ActionType.SetShareScreen;
      isSharingScreen: boolean;
    }
  | {
      type: ActionType.FirebaseUserReceived;
      user: firebase.User | null;
    }
  | {
      type: ActionType.SetSpotlightPosition;
      position: ISpotlightPosition;
    }
  | {
      type: ActionType.SetMainVideoPosition;
      position: ISpotlightPosition;
    }
  | {
      type: ActionType.SetFakeUsers;
      fakeUsers: IParticipantForUser[];
    }
  | {
      type: ActionType.MoveRandomFakeUser;
    }
  | {
      type: ActionType.SetParty;
      party: IPartyForUser;
    }
  | {
      type: ActionType.SetIsAudioInputBlocked;
      isAudioInputBlocked: boolean;
    }
  | {
      type: ActionType.SetChatBoxVisible;
      isChatBoxVisible: boolean;
    }
  | {
      type: ActionType.ToggleAV;
      enabled: boolean;
    };

const initialState: IAppContextState = {
  isTwitchChatOpen: false,
  user: null,
  masterVolume: 1,
  masterMuted: false,
  enableAV: true,
  authState: AuthState.LoggedOut,
  cockpitMode: false,
  spotlightPosition: {
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    ref: null,
  },
  mainVideoPosition: {
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    ref: null,
  },
  fakeUsers: [],
  party: null,
  isAudioInputBlocked: false,
  isSharingScreen: false,
  isChatBoxVisible: true,
};

const groupParticipantsByAreas = (participants: IParticipantForUser[]): TParticipantsByArea =>
  participants.reduce(
    (byArea, p) => {
      byArea[p.currentArea] = [...(byArea[p.currentArea] || []), p].sort((a, b) =>
        a.uid.localeCompare(b.uid)
      );
      return byArea;
    },
    // These areas always exist
    {
      [(EAreaPrefix.ChatPrefix + '-0') as TAreaKey]: [],
      [(EAreaPrefix.ChatPrefix + '-1') as TAreaKey]: [],
      [(EAreaPrefix.ChatPrefix + '-2') as TAreaKey]: [],
      [(EAreaPrefix.LobbyPrefix + '-0') as TAreaKey]: [],
    } as TParticipantsByArea
  );

const getSavableState = ({
  isTwitchChatOpen,
  isAudioInputBlocked,
  masterVolume,
  masterMuted,
}: IAppContextState) => ({
  isTwitchChatOpen: isTwitchChatOpen || false,
  isAudioInputBlocked: isAudioInputBlocked === undefined ? true : isAudioInputBlocked,
  masterMuted: masterMuted || false,
  masterVolume: masterVolume !== undefined && masterVolume !== null ? masterVolume : 1,
});

function useLocalStorage(newState: IAppContextState) {
  const prevState = useRef<IAppContextState | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveToStorage = useCallback(
    throttle((state: IAppContextState) => {
      localStorage.setItem('APP_STATE', JSON.stringify(getSavableState(state)));
    }, 200),
    []
  );

  useEffect(() => {
    if (
      prevState.current &&
      JSON.stringify(getSavableState(newState)) !==
        JSON.stringify(getSavableState(prevState.current))
    ) {
      console.log('saving');
      saveToStorage(newState);
    }
    prevState.current = newState;
  }, [prevState, newState, saveToStorage]);
}

function getInitialState() {
  const stateJson = localStorage.getItem('APP_STATE');
  if (typeof stateJson === 'string') {
    return {
      ...initialState,
      ...getSavableState(JSON.parse(stateJson)),
    };
  }
  return initialState;
}

function reducer(state: IAppContextState, action: TAction) {
  switch (action.type) {
    case ActionType.SetTwitchChatOpenState:
      return {
        ...state,
        isTwitchChatOpen: action.isTwitchChatOpen,
      };
    case ActionType.SetUser:
      return {
        ...state,
        user: action.user || null,
        authState: action.user ? AuthState.LoggedIn : AuthState.LoggedOut,
      };
    case ActionType.FetchFirebaseAuth:
      return {
        ...state,
        authState: AuthState.FetchingUser,
      };
    case ActionType.FirebaseUserReceived:
      return {
        ...state,
        authState: action.user ? AuthState.FetchingUser : AuthState.LoggedOut,
      };
    case ActionType.UpdateUser: {
      if (state.user) {
        return {
          ...state,
          user: {
            ...state.user,
            ...action.user,
          },
          authState: AuthState.LoggedIn,
        };
      }
      return state;
    }
    case ActionType.SetVolume:
      return {
        ...state,
        masterVolume: action.volume,
        masterMuted: false,
      };
    case ActionType.MuteMaster:
      return {
        ...state,
        masterMuted: action.muted,
      };
    case ActionType.SetChatBoxVisible:
      return {
        ...state,
        isChatBoxVisible: action.isChatBoxVisible,
      };
    case ActionType.SetShareScreen:
      return {
        ...state,
        isSharingScreen: action.isSharingScreen,
      };
    case ActionType.ToggleAV:
      return {
        ...state,
        enableAV: action.enabled,
      };
    case ActionType.ToggleCockpitMode:
      return {
        ...state,
        cockpitMode: action.enable,
      };
    case ActionType.SetIsAudioInputBlocked:
      return {
        ...state,
        isAudioInputBlocked: action.isAudioInputBlocked,
      };
    case ActionType.SetSpotlightPosition:
      return {
        ...state,
        spotlightPosition: action.position,
      };
    case ActionType.SetMainVideoPosition:
      return {
        ...state,
        mainVideoPosition: action.position,
      };
    case ActionType.SetParty:
      return {
        ...state,
        party: action.party,
      };
    case ActionType.SetUserDevAdmin:
      if (state.user?.isAdmin !== undefined) {
        return {
          ...state,
          user: {
            ...state.user,
            isAdmin: action.isAdmin,
          },
        };
      }
      return state;
    case ActionType.InviteFakeUserToArea: {
      const fakeUser = state.fakeUsers.find((p) => p.uid === action.fakeUserId);
      if (!fakeUser) {
        console.error('Fake user not found');
        return state;
      }
      fakeUser.currentArea = action.areaKey;
      return {
        ...state,
        fakeUsers: [...state.fakeUsers],
      };
    }
    case ActionType.SetFakeUsers:
      return {
        ...state,
        fakeUsers: action.fakeUsers,
      };
    case ActionType.MoveRandomFakeUser: {
      const oldFakies = state.fakeUsers;
      const participants = [...Object.values(state.party?.stage?.participants || {}), ...oldFakies];

      if (oldFakies.length) {
        const newFakies = [...state.fakeUsers];
        const participantsByArea = groupParticipantsByAreas(participants);
        const randIdx = Math.floor(Math.random() * oldFakies.length);
        const fakeParticipantToMove = oldFakies[randIdx];

        const availableAreas = Object.entries(participantsByArea)
          .filter(
            ([key, areaParticipants]) =>
              key !== fakeParticipantToMove.currentArea && areaParticipants.length < MAX_PEERS
          )
          .map(([key, __]) => key)
          .concat(DEMO_AREAS)
          .concat([`${EAreaPrefix.ChatPrefix}-${Math.round(Math.random() * 10000)}`])
          .filter((v, i, a) => a.indexOf(v) === i);

        newFakies[randIdx] = {
          ...newFakies[randIdx],
          currentArea: availableAreas[
            Math.round(Math.random() * (availableAreas.length - 1))
          ] as TAreaKey,
        };

        return {
          ...state,
          fakeUsers: newFakies,
        };
      }

      return state;
    }
    case ActionType.ResetStore:
      return getInitialState();
    default:
      throw new Error();
  }
}

function useFirebaseAuth(dispatch: Dispatch<TAction>, amplitude?: amplitudeJs.AmplitudeClient) {
  const client = useApolloClient();

  useEffect(() => {
    dispatch({ type: ActionType.FetchFirebaseAuth });
  }, [dispatch]);

  useEffect(() => {
    let isCanceled = false;
    const unregisterAuth = firebase.auth().onAuthStateChanged((firebaseUser) => {
      dispatch({ type: ActionType.FirebaseUserReceived, user: firebaseUser });
      if (IS_PRODUCTION && firebaseUser?.uid) {
        firebase?.analytics().setUserId(firebaseUser?.uid || 'unknown');
        amplitude?.setUserId(firebaseUser.uid);
      }

      if (!isCanceled && firebaseUser) {
        Promise.all([
          client.query({
            query: gql`
              query GetUser {
                user {
                  displayName
                  photoURL
                  hasAcceptedProfile
                  avatarUrl
                }
              }
            `,
            variables: {},
          }),
          firebaseUser?.getIdTokenResult(),
        ]).then(([getUserResult, IDTokenResult]) => {
          if (!isCanceled) {
            const user: IUser = getUserResult.data.user;
            dispatch({
              type: ActionType.SetUser,
              user: {
                ...firebaseUser,
                ...user,
                isAdmin: process.env.NODE_ENV === 'development' || IDTokenResult.claims.admin,
              },
            });
          }
        });
      } else {
        if (!isCanceled) {
          dispatch({
            type: ActionType.SetUser,
            user: null,
          });
        }
      }
    });
    return () => {
      isCanceled = true;
      unregisterAuth();
    };
  }, [dispatch, client, amplitude]);
}

// const useAnalytics = () => {
//   const [analytics, setAnalytics] = useState<firebase.analytics.Analytics>();
//   useEffect(() => {
//     if (IS_PRODUCTION) {
//       const newApp = firebase?.analytics();
//       setAnalytics(newApp);
//     }
//   }, []);
//   return {
//     analytics,
//   };
// };

const useAmplitudeAnalytics = () => {
  const [amplitude, setAmplitude] = useState<amplitude.AmplitudeClient>();
  useEffect(() => {
    if (IS_PRODUCTION) {
      const newApp = amplitudeJs.getInstance();
      newApp.init('e78ea740eb20592443bb2016f8c545dc');
      newApp.logEvent('loaded_page');
      setAmplitude(newApp);
    }
  }, []);
  return {
    amplitude,
  };
};

// const useSettings = () => {
//   useEffect(() => {
//     return onSettingsUpdate(SETTING_MIN_APP_VERSION, (minAppVersion) => {
//       if (minAppVersion && compareVersions(minAppVersion, APP_VERSION) > 0) {
//         window.location.reload(false);
//       }
//     });
//   }, []);
// };

export function AppStateProvider({ children }: { children?: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const { amplitude } = useAmplitudeAnalytics();
  useFirebaseAuth(dispatch, amplitude);
  useLocalStorage(state);

  const logOut = useCallback(async () => {
    await onLogOut();
    await dispatch({ type: ActionType.ResetStore });
  }, [dispatch]);

  const realParticipants: IParticipantForUser[] = Object.values(
    state.party?.stage?.participants || {}
  );

  const allParticipants = useMemo(
    () =>
      [...realParticipants, ...state.fakeUsers]
        .filter((p) => p.active || (p.tags || []).includes('banned'))
        .sort((a, b) => (a.active ? 0 : 1) - (b.active ? 0 : 1)),
    [realParticipants, state.fakeUsers]
  );

  const activeParticipants = allParticipants.filter(
    (p) => !(p.tags || []).includes('banned') && p.active
  );

  const participantsByArea: TParticipantsByArea = useMemo(() => {
    const pByA = groupParticipantsByAreas(activeParticipants);

    // Hack to avoid empty area keys...
    // Maybe happens because participant has not joined an area yet
    // causes crash when trying to joing
    delete pByA[''];

    return pByA;
  }, [activeParticipants]);

  const currentParticipant = state.party?.stage.participants[state.user?.uid as TUserId] || null;
  const isHost = state.user?.isAdmin || currentParticipant?.tags?.includes('host') || false;
  const isMod = state.user?.isAdmin || currentParticipant?.tags?.includes('moderator') || false;

  const inviteParticipantToArea = useCallback(
    async (inviteeId) => {
      if (!state.user) {
        LOG_ERROR(
          'inviteUserToModal',
          new Error(`Cannot invite user ${inviteeId} since current user does not exist`)
        );
        return;
      }

      if (!state.party) {
        LOG_ERROR(
          'inviteUserToModal',
          new Error(`Cannot invite user ${inviteeId} since current party does not exist`)
        );
        return;
      }

      const currentUserId = state.user.uid as TUserId;
      const currentArea = state.party?.stage?.participants?.[currentUserId]
        ?.currentArea as TAreaKey;

      if (!currentArea) {
        LOG_ERROR(
          'inviteUserToModal',
          new Error(`currentArea does not exist: ${currentArea} for userId: ${currentUserId}`)
        );
        return;
      }

      if (state.fakeUsers.find((p) => p.uid === inviteeId)) {
        dispatch({
          type: ActionType.InviteFakeUserToArea,
          fakeUserId: inviteeId,
          areaKey: currentArea,
        });
        return;
      }

      await clientInviteParticipantToArea({
        idToken: await getIdToken(),
        inviteeId,
        area: currentArea,
        key: state.party?.key,
      });
    },
    [state.party, state.user, state.fakeUsers]
  );

  return (
    <AppStateContext.Provider
      value={{
        ...state,
        masterVolume: state.masterMuted ? 0 : state.masterVolume,
        participants: activeParticipants,
        allParticipants,
        participantsByArea,
        currentArea: (state.party?.stage.participants[state.user?.uid as TUserId]?.currentArea ||
          '') as TAreaKey,
        currentParticipant,
        isHost,
        isMod,
        inviteParticipantToArea,
        logOut,
        dispatch,
      }}>
      {children}
    </AppStateContext.Provider>
  );
}

const AppStateContext = createContext<
  IAppContextState & { dispatch: Dispatch<TAction>; logOut: () => Promise<void> } & IDerivedAppState
>({
  ...initialState,
  participants: [],
  allParticipants: [],
  participantsByArea: {},
  currentArea: '' as TAreaKey,
  currentParticipant: null,
  isHost: false,
  isMod: false,
  inviteParticipantToArea: (inviteeId: TUserId) => Promise.resolve(),
  dispatch: (() => null) as Dispatch<TAction>,
  logOut: () => Promise.resolve(),
});

export const useAppState = () => {
  return useContext(AppStateContext);
};
